import { useRef, useState, type DragEvent, type FormEvent } from 'react';
import { ContactConsentBox } from './ContactConsent.js';
import { MAX_DOCS, validateUpload } from '../upload.js';
import type { ProjectSummary } from '../api.js';

// The ingestion-loop front door (ADR-0020 Phase 1): drop a deal document package → the worker
// ingests it into Nexus, extracts the inputs, and underwrites it. This component owns the file
// selection + reviewer context + the optional project assignment; AppShell reads the files to base64
// and enqueues the run.

export interface ProjectAssignment {
  code: string;
  name?: string | null;
}

interface DocUploadProps {
  submitting: boolean;
  error: string | null;
  /** The tenant's known projects (GET /projects) — picker options. Empty → straight to "new project". */
  projects: ProjectSummary[];
  onSubmit: (payload: {
    files: File[];
    reviewerContext: string;
    project: ProjectAssignment | null;
    /** The asserted sponsor name — the SEC EDGAR search key. Empty string when not supplied. */
    sponsorName: string;
    /** The asserted sponsor evidence — the Sponsor module's corpus. Empty string when not supplied. */
    sponsorContext: string;
    /** Ground Truth §2.4 — the client's answer to the contact box. Always sent from this surface. */
    contactConsent: boolean;
  }) => void;
}

/** Sentinel select values distinct from any real project code. */
const NEW_PROJECT = '__new__';

const ACCEPT = '.pdf,.txt,.md,.csv,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.tiff';

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function DocUpload({ submitting, error, projects, onSubmit }: DocUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [reviewerContext, setReviewerContext] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorContext, setSponsorContext] = useState('');
  // The dropdown selection: '' = unassigned, NEW_PROJECT = enter a new one, else an existing code.
  // With no known projects we skip the dropdown and go straight to the new-project inputs.
  /** Records this picker can actually assign to — see the note on the select below (#653-adjacent). */
  const numberedProjects = projects.filter((p) => typeof p.code === 'string' && p.code !== '');
  const unnumberedCount = projects.length - numberedProjects.length;
  const [selected, setSelected] = useState(projects.length > 0 ? '' : NEW_PROJECT);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // Ground Truth §2.4: off by default, never pre-selected. Per-submission state, not seeded.
  const [contactConsent, setContactConsent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function addFiles(incoming: FileList | null) {
    if (!incoming || incoming.length === 0) return;
    setFiles((prev) => {
      // De-dupe by name+size so re-dropping the same file doesn't double it.
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const merged = [...prev];
      for (const f of Array.from(incoming)) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(f);
        }
      }
      setLocalError(validateUpload(merged));
      return merged;
    });
  }

  function removeFile(index: number) {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      setLocalError(next.length === 0 ? null : validateUpload(next));
      return next;
    });
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (!submitting) addFiles(e.dataTransfer.files);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const validation = validateUpload(files);
    if (validation) {
      setLocalError(validation);
      return;
    }
    onSubmit({
      files,
      reviewerContext,
      project: resolveProject(),
      sponsorName,
      sponsorContext,
      contactConsent,
    });
  }

  // Map the picker state to the assignment payload: an existing project carries its own name; a new
  // one comes from the typed inputs (blank code → unassigned). Code is the folder key.
  function resolveProject(): ProjectAssignment | null {
    if (selected === NEW_PROJECT) {
      const code = newCode.trim();
      return code ? { code, name: newName.trim() || null } : null;
    }
    if (selected === '') return null;
    const existing = projects.find((p) => p.code === selected);
    return { code: selected, name: existing?.name ?? null };
  }

  return (
    <form className="df" onSubmit={handleSubmit}>
      <div
        className={`dz${dragging ? ' dz--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!submitting) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !submitting && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !submitting) inputRef.current?.click();
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="dz__input"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = ''; // allow re-selecting the same file later
          }}
        />
        <div className="dz__icon" aria-hidden="true">
          ⬆
        </div>
        <p className="dz__lead">
          <strong>Drop deal documents here</strong> or click to browse
        </p>
        <p className="dz__hint">
          Proformas, rent rolls, OMs, lender packages — PDF, Word, Excel, or text. Up to {MAX_DOCS}{' '}
          files. We ingest, extract the inputs, and underwrite automatically.
        </p>
      </div>

      {files.length > 0 && (
        <ul className="filelist">
          {files.map((f, i) => (
            <li className="filelist__item" key={`${f.name}:${f.size}:${i}`}>
              <span className="filelist__name" title={f.name}>
                {f.name}
              </span>
              <span className="filelist__size">{fmtSize(f.size)}</span>
              <button
                type="button"
                className="filelist__remove"
                aria-label={`Remove ${f.name}`}
                onClick={() => removeFile(i)}
                disabled={submitting}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <fieldset className="df__group">
        <legend>Project (optional)</legend>
        {projects.length > 0 && (
          <label className="df__field df__field--full">
            <span className="df__label">Assign to project</span>
            <span className="df__inputwrap">
              {/*
                ⚠️ NUMBERED RECORDS ONLY, and that is a LIMITATION, not a design.

                This picker assigns an upload by project CODE, and a pursuit has none until the IC
                promotes it (canon R-e). `ProjectSummary.code` is `string | null` for that reason, and
                rendering the null ones would put `value={null}` on an option — an entry that looks
                selectable and assigns the upload nowhere.

                The route already surfaces `id` expressly so an unnumbered record can still be picked
                ("a pursuit you cannot pick is a pursuit whose documents go somewhere else"). Wiring
                that through needs the upload API to accept an id, which is a server change and not
                this one. Filtering them out with a visible note keeps the gap legible instead of
                silent — a broken option would have mis-assigned quietly.
              */}
              <select value={selected} onChange={(e) => setSelected(e.target.value)}>
                <option value="">Unassigned</option>
                {numberedProjects.map((p) => (
                  <option key={p.code} value={p.code as string}>
                    {p.name ? `${p.name} (${p.code})` : p.code}
                  </option>
                ))}
                <option value={NEW_PROJECT}>+ New project…</option>
              </select>
            </span>
            {unnumberedCount > 0 && (
              <span className="df__help">
                {unnumberedCount} pursuit{unnumberedCount === 1 ? '' : 's'} not listed — a pursuit
                carries no project number until it is promoted, and uploads are assigned by number.
              </span>
            )}
          </label>
        )}
        {selected === NEW_PROJECT && (
          <div className="df__grid">
            <label className="df__field">
              <span className="df__label">Project code</span>
              <span className="df__inputwrap">
                <input
                  type="text"
                  placeholder="e.g. 2025-014"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                />
              </span>
            </label>
            <label className="df__field">
              <span className="df__label">Project name</span>
              <span className="df__inputwrap">
                <input
                  type="text"
                  placeholder="e.g. The Commons"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </span>
            </label>
          </div>
        )}
        <p className="muted">
          Assign this deal to a project and it files into that folder in the Deal Room — leave it
          unassigned to skip. Use the same code as the connector folder to group them.
        </p>
      </fieldset>

      {/*
        The SEC EDGAR search key, and the ONLY external check whose key a human has to supply — every
        other one is derived from the site. It belongs on THIS form specifically: the document path is
        the one that runs the external checks, and a package carries no sponsor identity we will trust
        (we deliberately do not extract it, because a mis-extracted name matches the wrong real company).
        Without this field the lookup can never run from the UI at all.
      */}
      <label className="df__field df__field--full">
        <span className="df__label">Sponsor / borrower (optional)</span>
        <span className="df__inputwrap">
          <input
            type="text"
            placeholder="e.g. Ridgeline Development LLC — or a principal's name"
            value={sponsorName}
            onChange={(e) => setSponsorName(e.target.value)}
          />
        </span>
        <span className="df__hint">
          Searches public SEC filings for entities matching this name. A person’s name works too —
          Form D lists its related persons, so it finds the entities they filed under. Leave blank
          to skip.
        </span>
      </label>

      {/*
        Sponsor EVIDENCE — a different field from the name above, and it belongs on this form for a
        reason the name's comment already establishes: extraction does not look for sponsor material,
        so an uploaded package supplies none of it. A package that CONTAINS a development-team section
        still lands here empty until RAG retrieval reads it (ADR-0025). Until then this is the only
        channel by which a document-path run can evidence 20 of its 100 points.
      */}
      <label className="df__field df__field--full">
        <span className="df__label">Sponsor &amp; development team (optional)</span>
        <textarea
          rows={3}
          placeholder="What have they built? e.g. Ridgeline has delivered 9 multifamily projects since 2011 (2,400 units, Raleigh–Durham). Principals: J. Marek (28 yrs), A. Solano (16 yrs). GC: Hoffman Construction — 6 prior projects together."
          value={sponsorContext}
          onChange={(e) => setSponsorContext(e.target.value)}
        />
        <span className="df__hint">
          Scored — this is the evidence behind Sponsor &amp; Execution, 20 of the 100 points. It is
          not read from your documents, so left blank that category is judged with no sponsor
          information and reported as unevidenced.
        </span>
      </label>

      <label className="df__field df__field--full">
        <span className="df__label">Reviewer context (optional)</span>
        <textarea
          rows={2}
          // ⚠️ No longer says "sponsor track record" / "GC relationship" — this field is a class/amenity
          // override and never reaches the Sponsor module. See the matching note in DealForm.tsx.
          placeholder="Market notes, asset positioning, anything the analyst should weigh…"
          value={reviewerContext}
          onChange={(e) => setReviewerContext(e.target.value)}
        />
      </label>

      {localError && <div className="notice error">{localError}</div>}
      {error && <div className="notice error">{error}</div>}

      <ContactConsentBox
        checked={contactConsent}
        onChange={setContactConsent}
        disabled={submitting}
      />

      <button type="submit" disabled={submitting || files.length === 0}>
        {submitting ? 'Uploading & analyzing…' : 'Analyze documents'}
      </button>
    </form>
  );
}
